import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SettingsStartMobile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const go = (path: string) => navigate(path);

  return (
    <div style={pageShell}>

      {/* Main content */}
      <main style={mainStyle}>
        <header style={{ marginBottom: 20 }}>
          <h2
            style={{
              fontSize: 26,
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            {t('settingsOverview.title', 'Settings overview')}
          </h2>
          <p
            style={{
              color: '#9ca3af',
              fontSize: 13,
              maxWidth: 560,
            }}
          >
            {t(
              'settingsOverview.subtitle',
              'Manage your UNYDO account, privacy, security and profile tools from one central place.'
            )}
          </p>
        </header>

        {/* Cards grid */}
        <section style={gridStyle}>
          {/* Account */}
          <div style={cardStyle}>
            <div style={cardHeaderRow}>
              <span style={cardTitle}>
                👤 {t('settingsOverview.cards.account.title', 'Account')}
              </span>
              <span style={pillNeutral}>
                {t('settingsOverview.cards.account.pill', 'Basics')}
              </span>
            </div>
            <p style={cardText}>
              {t(
                'settingsOverview.cards.account.desc',
                'Update email and password, choose your app language and pause your profile visibility.'
              )}
            </p>
            <ul style={bulletList}>
              <li>{t('settingsOverview.cards.account.item1', 'Change email')}</li>
              <li>{t('settingsOverview.cards.account.item2', 'Change password')}</li>
              <li>{t('settingsOverview.cards.account.item3', 'Language & localization')}</li>
              <li>{t('settingsOverview.cards.account.item4', 'Pause public profile')}</li>
            </ul>
            <button
              style={primaryBtn}
              onClick={() => go('/settings/account')}
            >
              {t('settingsOverview.cards.account.cta', 'Open Account settings')}
            </button>
          </div>

          {/* Data & Privacy */}
          <div style={cardStyle}>
            <div style={cardHeaderRow}>
              <span style={cardTitle}>
                🔒 {t('settingsOverview.cards.privacy.title', 'Data & privacy')}
              </span>
              <span style={pillWarn}>
                {t('settingsOverview.cards.privacy.pill', 'Important')}
              </span>
            </div>
            <p style={cardText}>
              {t(
                'settingsOverview.cards.privacy.desc',
                'Control location access, push notifications, anonymous analytics and manage your stored data.'
              )}
            </p>
            <ul style={bulletList}>
              <li>{t('settingsOverview.cards.privacy.item1', 'Allow or disable location fetching')}</li>
              <li>{t('settingsOverview.cards.privacy.item2', 'Push notification preferences')}</li>
              <li>{t('settingsOverview.cards.privacy.item3', 'Anonymous analytics opt-in/out')}</li>
              <li>{t('settingsOverview.cards.privacy.item4', 'View all stored data')}</li>
              <li>{t('settingsOverview.cards.privacy.item5', 'Privacy policy & account deletion')}</li>
            </ul>
            <button
              style={primaryBtn}
              onClick={() => go('/settings/privacy')}
            >
              {t('settingsOverview.cards.privacy.cta', 'Open Data & privacy')}
            </button>
          </div>

          {/* Security */}
          <div style={cardStyle}>
            <div style={cardHeaderRow}>
              <span style={cardTitle}>
                🛡️ {t('settingsOverview.cards.security.title', 'Security')}
              </span>
              <span style={pillAccent}>
                {t('settingsOverview.cards.security.pill', 'Protection')}
              </span>
            </div>
            <p style={cardText}>
              {t(
                'settingsOverview.cards.security.desc',
                'Add extra protection with 2-factor authentication and manage active logins across devices.'
              )}
            </p>
            <ul style={bulletList}>
              <li>{t('settingsOverview.cards.security.item1', 'Enable / manage 2-factor authentication')}</li>
              <li>{t('settingsOverview.cards.security.item2', 'See your 2FA recovery codes')}</li>
              <li>{t('settingsOverview.cards.security.item3', 'Log out from all devices')}</li>
            </ul>
            <button
              style={primaryBtn}
              onClick={() => go('/settings/security')}
            >
              {t('settingsOverview.cards.security.cta', 'Open Security settings')}
            </button>
          </div>

          {/* Profile management */}
          <div style={cardStyle}>
            <div style={cardHeaderRow}>
              <span style={cardTitle}>
                🧩 {t('settingsOverview.cards.profileMgmt.title', 'Profile management')}
              </span>
              <span style={pillNeutral}>
                {t('settingsOverview.cards.profileMgmt.pill', 'Activity')}
              </span>
            </div>
            <p style={cardText}>
              {t(
                'settingsOverview.cards.profileMgmt.desc',
                'See posts, followers and following, review notifications and manage your blocked users.'
              )}
            </p>
            <ul style={bulletList}>
              <li>{t('settingsOverview.cards.profileMgmt.item1', 'See your own postings')}</li>
              <li>{t('settingsOverview.cards.profileMgmt.item2', 'Notifications overview')}</li>
              <li>{t('settingsOverview.cards.profileMgmt.item3', 'Followers & following lists')}</li>
              <li>{t('settingsOverview.cards.profileMgmt.item4', 'Blocked users management')}</li>
            </ul>
            <button
              style={primaryBtn}
              onClick={() => go('/settings/profile-management')}
            >
              {t('settingsOverview.cards.profileMgmt.cta', 'Open Profile management')}
            </button>
          </div>

          {/* Help & support */}
          <div style={cardStyle}>
            <div style={cardHeaderRow}>
              <span style={cardTitle}>
                🛟 {t('settingsOverview.cards.help.title', 'Help & support')}
              </span>
              <span style={pillNeutral}>
                {t('settingsOverview.cards.help.pill', 'Support')}
              </span>
            </div>
            <p style={cardText}>
              {t(
                'settingsOverview.cards.help.desc',
                'Contact the UNYDO team if you need help, want to report a problem or give feedback.'
              )}
            </p>
            <ul style={bulletList}>
              <li>{t('settingsOverview.cards.help.item1', 'Contact UNYDO support')}</li>
              <li>{t('settingsOverview.cards.help.item2', 'Report issues or abuse')}</li>
              <li>{t('settingsOverview.cards.help.item3', 'Send feedback & ideas')}</li>
            </ul>
            <button
              style={primaryBtn}
              onClick={() => go('/helpsupportstart')} 
            >  
              {t('settingsOverview.cards.help.cta', 'Open Help & support')}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SettingsStartMobile;

/* ---------------------------- inline styles ---------------------------- */

const pageShell: React.CSSProperties = {
  display: 'flex',
  minHeight: '100vh',
  background: '#02040b',
};

const mainStyle: React.CSSProperties = {
  padding: '24px 15px 96px', // ⬅️ extra bottom space
  width: '100%',
  boxSizing: 'border-box',
  color: '#e5e7eb',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: 16,
};

const cardStyle: React.CSSProperties = {
  borderRadius: 14,
  border: '1px solid rgba(148,163,184,0.35)',
  background: 'rgba(15,23,42,0.95)',
  padding: 16,
  boxShadow: '0 18px 38px rgba(0,0,0,0.7)',
  display: 'flex',
  flexDirection: 'column',
};

const cardHeaderRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 6,
};

const cardTitle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
};

const cardText: React.CSSProperties = {
  fontSize: 13,
  color: '#9ca3af',
  marginBottom: 10,
};

const bulletList: React.CSSProperties = {
  listStyle: 'disc',
  paddingLeft: 18,
  margin: '0 0 12px 0',
  color: '#cbd5f5',
  fontSize: 12,
  lineHeight: 1.4,
};

const primaryBtn: React.CSSProperties = {
  marginTop: 'auto',
  alignSelf: 'flex-start',
  padding: '7px 12px',
  borderRadius: 999,
  border: '1px solid rgba(250,250,250,0.25)',
  background:
    'linear-gradient(135deg, rgba(212,175,55,0.95), rgba(250,250,210,0.9))',
  color: '#111827',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const pillBase: React.CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 999,
  border: '1px solid transparent',
};

const pillNeutral: React.CSSProperties = {
  ...pillBase,
  background: 'rgba(148,163,184,0.16)',
  borderColor: 'rgba(148,163,184,0.4)',
  color: '#e5e7eb',
};

const pillWarn: React.CSSProperties = {
  ...pillBase,
  background: 'rgba(248, 181, 83, 0.1)',
  borderColor: 'rgba(248, 181, 83, 0.6)',
  color: '#fed7aa',
};

const pillAccent: React.CSSProperties = {
  ...pillBase,
  background: 'rgba(34,197,94,0.12)',
  borderColor: 'rgba(34,197,94,0.6)',
  color: '#bbf7d0',
};
